import { useRef, useState } from 'react'
import httpClient from '../config/http.client'
import { MFA } from '../constants/steps'
import { STATE_LOADING } from '../constants/button.states'
import { UNAUTHORIZED } from '../constants/http.codes'
import { AUTHENTICATE } from '../constants/links'
import redirect from '../utils/redirect'

const useLogin = ({
  ctx,
  email,
  showCaptcha: showCaptchaProp,
  siteKey,
  csrf,
  handleErrorChange,
  setIdentityProvidersDisabled,
}) => {
  const [username, setUsername] = useState(email)
  const [password, setPassword] = useState('')
  const [validUsername, setValidUsername] = useState(email !== '')
  const [validPassword, setValidPassword] = useState(false)
  const [recaptchaResponse, setRecaptchaResponse] = useState('')
  const [disableForm, setDisableForm] = useState(false)
  const [showCaptcha, setShowCaptcha] = useState(showCaptchaProp)
  const [loginButtonState, setLoginButtonState] = useState(undefined)
  const recaptchaInstanceRef = useRef(undefined)

  const isLoginButtonDisabled =
    disableForm ||
    !(
      validUsername &&
      validPassword &&
      (!showCaptcha || recaptchaResponse || siteKey === '')
    )

  const resetRecaptcha = () => {
    if (recaptchaInstanceRef.current) {
      recaptchaInstanceRef.current.reset()
    }
  }

  const handleError = (newShowCaptcha) => {
    if (recaptchaResponse) {
      resetRecaptcha()
    }

    handleErrorChange()
    setIdentityProvidersDisabled(false)

    setUsername(email)
    setPassword('')
    setValidUsername(email !== '')
    setValidPassword(false)
    setRecaptchaResponse('')
    setDisableForm(false)
    setLoginButtonState(null)
    if (newShowCaptcha !== undefined) {
      setShowCaptcha(
        (previousShowCaptcha) => previousShowCaptcha || newShowCaptcha,
      )
    }
  }

  const handleUsernameChange = (event) => {
    const { value } = event.target
    ctx.updateCredentials('username', value)
    setUsername(value)
    setValidUsername(!!value.length)
  }

  const handlePasswordChange = (event) => {
    const { value } = event.target
    ctx.updateCredentials('password', value)
    setPassword(value)
    setValidPassword(!!value.length)
  }

  const handleCaptchaResponse = (response) => setRecaptchaResponse(response)

  const handleLoginResponse = (response) => {
    redirect(response.data.redirect_uri)
  }

  const handleLoginError = (error) => {
    const { response } = error
    if (response?.status === UNAUTHORIZED) {
      const json = response.data || {}
      if (json.redirect_uri) {
        redirect(json.redirect_uri)
      } else if (json.enabled_mfa && json.enabled_mfa.length) {
        ctx.setMFAs(json.enabled_mfa)
        ctx.changeStep(MFA)
      } else {
        handleError(json.show_captcha)
      }
    } else {
      handleError()
    }
  }

  const handleSubmit = () => {
    setLoginButtonState(STATE_LOADING)
    setDisableForm(true)
    setIdentityProvidersDisabled(true)

    const data = { _csrf: csrf.token, username, password }
    if (recaptchaResponse) {
      data['g-recaptcha-response'] = recaptchaResponse
    }

    httpClient
      .post(AUTHENTICATE, new URLSearchParams(data).toString())
      .then(handleLoginResponse)
      .catch(handleLoginError)
  }

  return {
    username,
    password,
    showCaptcha,
    disableForm,
    loginButtonState,
    isLoginButtonDisabled,
    recaptchaInstanceRef,
    handleUsernameChange,
    handlePasswordChange,
    handleCaptchaResponse,
    handleSubmit,
  }
}

export default useLogin
